import type {
  CalculatedDimension,
  StrategyOptions,
  StrategyResult,
} from './types'

/** Resolves '10%' to size * 0.1
 * -10 to size - 10
 * 10 to 10
 */
export function resolveDimension(
  value: CalculatedDimension,
  size: number,
  options: StrategyOptions
): number {
  if (typeof value === 'function') return value(size, options)
  const floatValue = parseFloat(value)
  if (typeof value === 'string' && value[value.length - 1] === '%') {
    return (floatValue / 100) * size
  }
  if (isNaN(floatValue)) {
    throw new Error(
      `Dimension should be a function, numeric or %. "${value}" is not recognized`
    )
  }
  if (floatValue < 0) return size + floatValue
  return floatValue
}

export function clampDimension(
  value: CalculatedDimension,
  size: number,
  options: any
): number {
  return Math.max(0, Math.min(size, resolveDimension(value, size, options)))
}

function axisAlign(keys, options): StrategyResult {
  const {pos: posKey, size: sizeKey, axisSuffix} = keys
  const anchorPadding = options.anchorPadding || 0
  const targetPos = options.targetRect[posKey]
  const targetSize = options.targetRect[sizeKey]
  const elementSize = options.elementRect[sizeKey]
  const allowShift =
    options[`allowShift${axisSuffix}`] !== undefined
      ? options[`allowShift${axisSuffix}`]
      : options.allowShift
  const anchor =
    resolveDimension(
      options[`anchor${axisSuffix}`] || 0,
      targetSize - 2 * anchorPadding,
      options
    ) + anchorPadding
  const align = resolveDimension(
    options[`align${axisSuffix}`] || 0,
    targetSize - elementSize,
    options
  )

  // Element position as requested by alignment
  const requestedPos = targetPos + align

  // Final element position to use
  let finalPos = requestedPos

  const containerRect = options.containerRect
  if (containerRect) {
    // Keep element inside the container
    const containerPos = containerRect[posKey]
    const containerSize = containerRect[sizeKey]
    finalPos = Math.min(finalPos, containerPos + containerSize - elementSize)
    finalPos = Math.max(finalPos, containerPos)
  }

  if (allowShift !== undefined && allowShift !== null) {
    // Element position adjusted to align with the target, so that they intersect as much as possible
    let alignedPos = finalPos
    if (targetSize > elementSize) {
      alignedPos = Math.max(alignedPos, targetPos)
      alignedPos = Math.min(alignedPos, targetPos + targetSize - elementSize)
    } else {
      alignedPos = Math.max(alignedPos, targetPos + targetSize - elementSize)
      alignedPos = Math.min(alignedPos, targetPos)
    }

    // Limit how far the element can be shifted from it's requested position
    // The base dimension is distance between requested and aligned positions
    const maxPosShift = Math.max(
      0,
      resolveDimension(allowShift, Math.abs(requestedPos - alignedPos), options)
    )
    finalPos = Math.max(requestedPos - maxPosShift, finalPos)
    finalPos = Math.min(requestedPos + maxPosShift, finalPos)
  }

  // Anchor is a position on the target, but it's relative to element
  const anchorPos = targetPos - finalPos + anchor
  // clamp anchor to element bounds, so it does not stick out
  const finalAnchorPos = Math.min(
    Math.max(anchorPos, anchorPadding),
    elementSize - anchorPadding
  )

  const result = {
    [posKey]: finalPos,
    [`anchor${axisSuffix}`]: finalAnchorPos,
    [`shifted${axisSuffix}`]: finalPos - requestedPos,
  }
  // @ts-ignore
  return result
}

function clampSize(
  _result: StrategyResult,
  options: StrategyOptions
): StrategyResult {
  const {
    containerRect,
    targetRect,
    minWidth,
    minHeight,
    maxWidth,
    maxHeight,
    width,
    height,
  } = options
  if (
    !containerRect &&
    minWidth === undefined &&
    minHeight === undefined &&
    maxWidth === undefined &&
    maxHeight === undefined &&
    width === undefined &&
    height === undefined
  ) {
    return _result
  }
  const result = {..._result}

  if (width !== undefined) {
    result.width = resolveDimension(width, targetRect.width, options)
  }
  if (height !== undefined) {
    result.height = resolveDimension(height, targetRect.height, options)
  }

  if (minWidth !== undefined) {
    result.minWidth = resolveDimension(minWidth, targetRect.width, options)
  }
  if (minHeight !== undefined) {
    result.minHeight = resolveDimension(minHeight, targetRect.height, options)
  }

  if (containerRect) {
    if (result.maxWidth === undefined) {
      result.maxWidth = Math.max(
        0,
        targetRect.left + targetRect.width - result.left,
        containerRect.left + containerRect.width - result.left
      )
    }
    if (result.maxHeight === undefined) {
      result.maxHeight = Math.max(
        0,
        targetRect.top + targetRect.height - result.top,
        containerRect.top + containerRect.height - result.top
      )
    }
  }

  if (maxWidth !== undefined) {
    const resolvedMaxWidth = Math.max(
      0,
      resolveDimension(maxWidth, targetRect.width, options)
    )
    result.maxWidth =
      result.maxWidth == null || isNaN(result.maxWidth)
        ? resolvedMaxWidth
        : Math.min(resolvedMaxWidth, result.maxWidth)
  }
  if (maxHeight !== undefined) {
    const resolvedMaxHeight = Math.max(
      0,
      resolveDimension(maxHeight, targetRect.height, options)
    )
    result.maxHeight =
      result.maxHeight == null || isNaN(result.maxHeight)
        ? resolvedMaxHeight
        : Math.min(resolvedMaxHeight, result.maxHeight)
  }

  return result
}

const horizontalKeys = {
  pos: 'left',
  size: 'width',
  minSize: 'minWidth',
  axisSuffix: 'X',
}
export function horizontalAlign(options: StrategyOptions): StrategyResult {
  return clampSize(axisAlign(horizontalKeys, options), options)
}

const verticalKeys = {
  pos: 'top',
  size: 'height',
  minSize: 'minHeight',
  axisSuffix: 'Y',
}
export function verticalAlign(options: StrategyOptions): StrategyResult {
  return clampSize(axisAlign(verticalKeys, options), options)
}

export function top(options: StrategyOptions): StrategyResult {
  const {targetRect, elementRect, containerRect} = options
  return clampSize(
    {
      ...axisAlign(horizontalKeys, options),
      placement: 'top',
      top: targetRect.top - elementRect.height,
      maxHeight: containerRect
        ? Math.max(0, targetRect.top - containerRect.top)
        : undefined,
    },
    options
  )
}

export function bottom(options: StrategyOptions): StrategyResult {
  const {targetRect, containerRect} = options
  return clampSize(
    {
      ...axisAlign(horizontalKeys, options),
      placement: 'bottom',
      top: targetRect.top + targetRect.height,
      maxHeight: containerRect
        ? Math.max(
            0,
            containerRect.top +
              containerRect.height -
              (targetRect.top + targetRect.height)
          )
        : undefined,
    },
    options
  )
}

export function left(options: StrategyOptions): StrategyResult {
  const {targetRect, elementRect, containerRect} = options
  return clampSize(
    {
      ...axisAlign(verticalKeys, options),
      placement: 'left',
      left: targetRect.left - elementRect.width,
      maxWidth: containerRect
        ? Math.max(0, targetRect.left - containerRect.left)
        : undefined,
    },
    options
  )
}

export function right(options: StrategyOptions): StrategyResult {
  const {targetRect, containerRect} = options
  return clampSize(
    {
      ...axisAlign(verticalKeys, options),
      placement: 'right',
      left: targetRect.left + targetRect.width,
      maxWidth: containerRect
        ? Math.max(
            0,
            containerRect.left +
              containerRect.width -
              (targetRect.left + targetRect.width)
          )
        : undefined,
    },
    options
  )
}

export function cover(options: StrategyOptions): StrategyResult {
  return clampSize(
    {
      ...axisAlign(horizontalKeys, options),
      ...axisAlign(verticalKeys, options),
      placement: 'cover',
    },
    options
  )
}

/** Strategy used to get min/max bounding rect that fits into the container
 * Only min/max width/height is taken into acount */
export function maximizeSize(options: StrategyOptions): StrategyResult {
  const rect = options.containerRect || options.elementRect
  if (rect.left === undefined || rect.top === undefined) {
    throw new Error('left/top values are missing from elementRect')
  }
  return clampSize(
    {
      left: rect.left,
      top: rect.top,
      placement: 'cover',
    },
    options
  )
}
